import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { fetchSystemSchools, subscriptionStatusColor } from "../../utils/systemApi";
import { getPlanById, SUBSCRIPTION_PLANS } from "../../config/subscriptionPlans";

export default function SchoolsList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [plan, setPlan] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchSystemSchools({
                q: q.trim() || undefined,
                status: status || undefined,
                plan: plan || undefined,
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not load schools");
        } finally {
            setLoading(false);
        }
    }, [q, status, plan]);

    useEffect(() => {
        const timer = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(timer);
    }, [load, q]);

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Registered schools</Typography>
                <Typography variant="body2" color="text.secondary">
                    Contact details, location, subscription status, and enrolment counts.
                </Typography>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                    label="Search schools"
                    placeholder="Name, director, email, district…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    fullWidth
                    size="small"
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="trialing">Trialing</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="lifetime">Legacy lifetime</MenuItem>
                        <MenuItem value="pending">Payment pending</MenuItem>
                        <MenuItem value="pending_verification">Awaiting verification</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Plan</InputLabel>
                    <Select label="Plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {SUBSCRIPTION_PLANS.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>School</TableCell>
                                <TableCell>Director</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Students</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const id = row.id || row._id;
                                return (
                                    <TableRow
                                        key={id}
                                        hover
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => navigate(`/System/schools/${id}`)}
                                    >
                                        <TableCell>{row.schoolName}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.contactPhone || "—"}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.contactAddress || "—"}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {[row.schoolDistrict, row.schoolRegion].filter(Boolean).join(", ") || "—"}
                                        </TableCell>
                                        <TableCell>{getPlanById(row.subscriptionPlan).name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={subscriptionStatusColor(row.subscriptionStatus)}
                                                label={row.subscriptionStatus}
                                            />
                                            {String(row.verificationStatus || "").toLowerCase() === "pending" && (
                                                <Chip size="small" color="warning" label="Awaiting verification" sx={{ ml: 0.5 }} />
                                            )}
                                            {row.paymentPendingAt && (
                                                <Chip size="small" color="warning" label="Pending pay" sx={{ ml: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{row.studentCount ?? 0}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        No schools match your filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Stack>
    );
}
